// src/pages/MeusIngressos.tsx — portal do comprador.
// Login por email + código OTP (SES, sem Cognito) via rotas POST /meus/*
// (Lambda `meusIngressos`). O código vale 90 min e é reenviado em cada ação.
// Sessão em localStorage (vale em novas abas); a expiração real é no servidor.
import React, { useCallback, useEffect, useState } from 'react';
import { Payment } from '@mercadopago/sdk-react';
import { customization } from '../services/paymentService';
import { precoDoIngresso, getLoteAtivo } from '../config/event';
import './LandingPage.css';
import './ListIngressos.css';

const API_BASE = 'https://ta3m3foyci.execute-api.sa-east-1.amazonaws.com';
const EMAIL_KEY = 'era_meus_email';
const CODE_KEY = 'era_meus_code';

type Step = 'email' | 'code' | 'authed';

interface Ingresso {
  ticket_number: number;
  nome: string;
  documento: string;
  email: string;
  telefone?: string;
  tipo_ingresso: string;
  documento_meia_entrada?: string | null;
  payment_id?: string;
  payment_approved: boolean;
  evento_id?: string;
}

async function meus(path: string, payload: unknown): Promise<any> {
  const response = await fetch(`${API_BASE}/meus/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err: any = new Error(
      (data && data.message) || 'Erro ao processar. Tente novamente.',
    );
    err.status = response.status;
    err.code = data && data.error;
    throw err;
  }
  return data;
}

const MeusIngressos: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [codigo, setCodigo] = useState('');
  const [ingressos, setIngressos] = useState<Ingresso[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [pdfLinks, setPdfLinks] = useState<{ [key: string]: string }>({});
  const [pdfLoading, setPdfLoading] = useState<{ [key: string]: boolean }>({});
  const [paying, setPaying] = useState<string | null>(null);
  const [pix, setPix] = useState<{ [paymentId: string]: { qr_code: string; qr_code_base64: string } }>({});
  const [copyOk, setCopyOk] = useState(false);
  const [expanded, setExpanded] = useState<string[]>([]);

  const logout = useCallback((message?: string) => {
    localStorage.removeItem(EMAIL_KEY);
    localStorage.removeItem(CODE_KEY);
    setCodigo('');
    setIngressos([]);
    setPdfLinks({});
    setPix({});
    setPaying(null);
    setExpanded([]);
    setStep('email');
    setInfo('');
    setError(message || '');
  }, []);

  const fetchLista = useCallback(
    async (em: string, cod: string) => {
      setIsLoading(true);
      setError('');
      setInfo('');
      try {
        const data = await meus('ingressos', { email: em, codigo: cod });
        const lista: Ingresso[] = Array.isArray(data) ? data : [];
        // Compras mais recentes primeiro (ticket_number cresce com o tempo).
        lista.sort((a, b) => b.ticket_number - a.ticket_number);
        setIngressos(lista);
        // Pedido mais recente começa expandido; demais colapsados.
        const firstPid = lista.length ? String(lista[0].payment_id || 'sem-pagamento') : null;
        setExpanded(firstPid ? [firstPid] : []);
        localStorage.setItem(EMAIL_KEY, em);
        localStorage.setItem(CODE_KEY, cod);
        setStep('authed');
        if (lista.length === 0) setInfo('Nenhum ingresso encontrado para este email.');
      } catch (err: any) {
        if (err?.status === 401 || err?.status === 404) {
          if (err?.status === 401) {
            localStorage.removeItem(CODE_KEY);
            setStep('code');
          } else {
            setIngressos([]);
            setStep('authed');
          }
        }
        setError(err?.message || 'Erro ao buscar ingressos. Tente novamente.');
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  // Sessão de 90 min (expiração real no servidor): tenta reaproveitar, 401 volta ao login.
  useEffect(() => {
    const em = localStorage.getItem(EMAIL_KEY) || '';
    const cod = localStorage.getItem(CODE_KEY) || '';
    if (em && cod) {
      setEmail(em);
      setCodigo(cod);
      fetchLista(em, cod);
    }
  }, [fetchLista]);

  const handleSendCode = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email.trim()) return;
    setIsLoading(true);
    setError('');
    setInfo('');
    try {
      await meus('solicitar-codigo', { email: email.trim() });
      setCodigo('');
      setStep('code');
      setInfo('Código enviado. Vale por 90 minutos — use sempre o último recebido. Não chegou em 5 minutos? Olhe o spam.');
    } catch (err: any) {
      setError(err?.message || 'Erro ao enviar o código. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmCode = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!codigo.trim()) return;
    setIsLoading(true);
    await fetchLista(email.trim(), codigo.trim());
    setIsLoading(false);
  };

  const handlePdf = async (ingresso: Ingresso) => {
    const key = String(ingresso.ticket_number);
    setPdfLoading((p) => ({ ...p, [key]: true }));
    setError('');
    try {
      const data = await meus('pdf', {
        email,
        codigo,
        ticket_number: ingresso.ticket_number,
      });
      const url = data.url as string;
      setPdfLinks((p) => ({ ...p, [key]: url }));
      window.open(url, '_blank', 'noopener');
    } catch (err: any) {
      if (err?.status === 401) logout('Código expirado. Peça um novo código.');
      else setError(err?.message || 'Erro ao gerar o PDF. Tente novamente.');
    } finally {
      setPdfLoading((p) => ({ ...p, [key]: false }));
    }
  };

  const handleCancelar = async (paymentId: string, qtd: number) => {
    if (!window.confirm(`Cancelar este pedido (${qtd} ingresso(s))? Só é possível antes do pagamento.`)) return;
    setIsLoading(true);
    setError('');
    try {
      const data = await meus('cancelar', { email, codigo, payment_id: paymentId });
      setInfo(`Pedido cancelado (${data.cancelados ?? qtd} ingresso(s)).`);
      await fetchLista(email, codigo);
    } catch (err: any) {
      if (err?.status === 401) logout('Código expirado. Peça um novo código.');
      else setError(err?.message || 'Erro ao cancelar. Tente novamente.');
      setIsLoading(false);
    }
  };

  const valorDoPedido = (itens: Ingresso[]): number => {
    const lote = getLoteAtivo();
    return itens.reduce((t, i) => t + precoDoIngresso(i.tipo_ingresso, lote), 0);
  };

  const formatBRL = (v: number) => `R$\u00A0${v.toFixed(2).replace('.', ',')}`;

  const copyPix = (code: string) => {
    if (!code) return;
    navigator.clipboard.writeText(code).then(() => {
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 3000);
    });
  };

  // Agrupa por pedido (payment_id) para ações por pedido.
  const pedidos: { [paymentId: string]: Ingresso[] } = {};
  for (const i of ingressos) {
    const pid = String(i.payment_id || 'sem-pagamento');
    (pedidos[pid] = pedidos[pid] || []).push(i);
  }

  const togglePedido = (pid: string) => {
    setExpanded((prev) => (prev.includes(pid) ? prev.filter((p) => p !== pid) : [...prev, pid]));
  };

  return (
    <div className="era-landing era-admin">
      <header className="era-header">
        <div className="era-header-inner">
          <a href="https://espacoera.store/" className="era-brand-link" title="Espaço ERA — Página Principal">
            <img src="/logo-era.jpg" alt="Logo Espaço ERA" className="era-header-logo" />
          </a>
          <button
            type="button"
            className="era-nav-toggle"
            aria-expanded={isMenuOpen}
            aria-controls="era-nav-principal"
            aria-label={isMenuOpen ? 'Fechar menu' : 'Abrir menu'}
            onClick={() => setIsMenuOpen((v) => !v)}
          >
            {isMenuOpen ? 'Fechar' : 'Menu'}
          </button>
          <nav id="era-nav-principal" className={`era-nav${isMenuOpen ? ' is-open' : ''}`} aria-label="Navegação principal">
            <a href="/" className="era-nav-link" onClick={() => setIsMenuOpen(false)}>Voltar ao site</a>
          </nav>
        </div>
      </header>

      <main className="era-admin-main">
        <div className="era-wrap">
          <p className="era-folio">Espaço ERA</p>
          <h1 className="era-h2">Meus ingressos</h1>

          {step !== 'authed' && (
            <p className="era-admin-sub">
              Informe seu email para receber um código de acesso e ver seus ingressos,
              gerar o PDF, pagar pedidos pendentes ou cancelar antes do pagamento.
            </p>
          )}
          {step === 'authed' && (
            <p className="era-admin-sub">
              Logado como {email} —{' '}
              <button type="button" className="era-cta-line" onClick={() => logout()}>
                Sair
              </button>
            </p>
          )}

          {step === 'email' && (
            <form className="era-admin-form era-admin-form-login" onSubmit={handleSendCode}>
              <div className="era-admin-field">
                <label className="era-admin-label" htmlFor="meusEmail">Email da compra</label>
                <input
                  id="meusEmail"
                  className="era-admin-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="voce@exemplo.com"
                  autoComplete="email"
                  required
                />
              </div>
              <button type="submit" className="era-primary-btn era-admin-submit" disabled={isLoading}>
                {isLoading ? 'Enviando' : 'Receber código'}
              </button>
            </form>
          )}

          {step === 'code' && (
            <form className="era-admin-form era-admin-form-login" onSubmit={handleConfirmCode}>
              <p className="era-admin-sub" role="status">
                Código enviado para {email}. Vale por 90 minutos. Não chegou em
                5 minutos? Olhe a caixa de spam ou lixo eletrônico.{' '}
                <button type="button" className="era-cta-line" onClick={() => { setStep('email'); setError(''); setInfo(''); }}>
                  Trocar email
                </button>
              </p>
              <div className="era-admin-field">
                <label className="era-admin-label" htmlFor="meusOtp">Código</label>
                <input
                  id="meusOtp"
                  className="era-admin-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value)}
                  placeholder="000000"
                  required
                />
              </div>
              <button type="submit" className="era-primary-btn era-admin-submit" disabled={isLoading}>
                {isLoading ? 'Verificando' : 'Ver meus ingressos'}
              </button>
              <button type="button" className="era-cta-line" onClick={handleSendCode} disabled={isLoading}>
                Reenviar código
              </button>
            </form>
          )}

          {error && <p className="era-admin-error" role="alert">{error}</p>}
          {info && !error && <p className="era-admin-sub" role="status">{info}</p>}

          {step === 'authed' && Object.entries(pedidos).map(([pid, itens]) => {
            const pago = itens.every((i) => i.payment_approved);
            const valor = valorDoPedido(itens);
            const aberto = expanded.includes(pid);
            const panelId = `pedido-ingressos-${pid}`;
            return (
              <section key={pid} className="era-admin-pedido" aria-label={`Pedido ${pid}`}>
                <div className="era-admin-pedido-resumo">
                  <h2 className="era-admin-pedido-titulo">
                    Pedido {pid === 'sem-pagamento' ? 'antigo' : `•••${pid.slice(-6)}`} — {itens.length} ingresso(s) — {formatBRL(valor)}{' '}
                    <span className={`era-admin-badge ${pago ? 'approved' : 'pending'}`}>
                      {pago ? 'Pago' : 'Pagamento pendente'}
                    </span>
                  </h2>
                  <button
                    type="button"
                    className="era-cta-line era-admin-toggle"
                    aria-expanded={aberto}
                    aria-controls={panelId}
                    onClick={() => togglePedido(pid)}
                  >
                    {aberto ? 'Ocultar' : `Detalhes (${itens.length})`}
                  </button>
                  {pago && pid !== 'sem-pagamento' && (
                    <a href={`/pedido/${pid}`} target="_blank" rel="noopener noreferrer" className="era-primary-btn era-admin-ver-todos">
                      Ver ingressos
                    </a>
                  )}
                </div>

                {aberto && (
                  <div id={panelId} role="region" aria-label={`Ingressos do pedido ${pid}`} className="era-admin-ingressos">
                    {itens.map((ingresso) => {
                      const key = String(ingresso.ticket_number);
                      return (
                        <article key={key} className="era-admin-ingresso-card" aria-label={`Ingresso ${ingresso.ticket_number}`}>
                          <div className="era-admin-ingresso-info">
                            <strong className="era-admin-ingresso-nome">{ingresso.nome}</strong>
                            <span className="era-admin-ingresso-meta">
                              Nº {ingresso.ticket_number} · {ingresso.tipo_ingresso}
                            </span>
                          </div>
                          <div className="era-admin-ingresso-acoes">
                            {ingresso.payment_approved ? (
                              <a href={`/ingresso/${ingresso.ticket_number}`} target="_blank" rel="noopener noreferrer" className="era-cta-line">
                                Ver ingresso
                              </a>
                            ) : (
                              <span className="era-admin-not-approved">Após o pagamento</span>
                            )}
                            {ingresso.payment_approved && (
                              <>
                                <button
                                  type="button"
                                  className="era-admin-resend"
                                  onClick={() => handlePdf(ingresso)}
                                  disabled={!!pdfLoading[key]}
                                >
                                  {pdfLoading[key] ? 'Gerando' : 'Baixar PDF'}
                                </button>
                                {pdfLinks[key] && (
                                  <a href={pdfLinks[key]} target="_blank" rel="noopener noreferrer" className="era-cta-line">
                                    Abrir
                                  </a>
                                )}
                              </>
                            )}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}

                {!pago && paying !== pid && !pix[pid] && (
                  <div className="era-admin-pedido-acoes">
                    <button type="button" className="era-primary-btn" onClick={() => { setPaying(pid); setError(''); }}>
                      Pagar agora • {formatBRL(valor)}
                    </button>
                    <button type="button" className="era-cta-line" onClick={() => handleCancelar(pid, itens.length)} disabled={isLoading}>
                      Cancelar pedido
                    </button>
                  </div>
                )}

                {!pago && pix[pid]?.qr_code_base64 && (
                  <div className="pix-box">
                    <div className="pix-qr-area">
                      <img
                        src={`data:image/png;base64,${pix[pid].qr_code_base64}`}
                        alt="QR Code Pix do pedido"
                        className="pix-qr-image"
                      />
                    </div>
                    <div className="pix-copy-area">
                      <label className="pix-label">Pix copia-e-cola</label>
                      <div className="pix-input-group">
                        <input type="text" className="pix-input" value={pix[pid].qr_code} readOnly />
                        <button type="button" onClick={() => copyPix(pix[pid].qr_code)} className="pix-copy-btn">
                          {copyOk ? 'Copiado' : 'Copiar código'}
                        </button>
                      </div>
                      <p className="pix-tip">Após pagar, o PDF chega no seu e-mail e o QR aparece aqui.</p>
                    </div>
                  </div>
                )}

                {!pago && paying === pid && (
                  <div className="payment-brick-wrapper">
                    <div className="brick-header">
                      <h3 className="brick-title">Pagamento via Mercado Pago</h3>
                      <p className="brick-subtitle">Pix com aprovação imediata ou cartão de crédito.</p>
                    </div>
                    <div className="brick-embed">
                      <Payment
                        initialization={{ amount: valor }}
                        customization={customization}
                        onSubmit={async (param) => {
                          setError('');
                          try {
                            const data = await meus('repagar', {
                              email,
                              codigo,
                              payment_id: pid,
                              param,
                            });
                            if (data.qr_code_base64) {
                              setPix((p) => ({
                                ...p,
                                [pid]: { qr_code: data.qr_code, qr_code_base64: data.qr_code_base64 },
                              }));
                              setPaying(null);
                            } else {
                              setInfo('Pagamento com cartão em processamento. O ingresso chega no seu e-mail após a confirmação.');
                              setPaying(null);
                              await fetchLista(email, codigo);
                            }
                          } catch (err: any) {
                            if (err?.status === 401) logout('Código expirado. Peça um novo código.');
                            else setError(err?.message || 'Erro no pagamento. Tente novamente.');
                          }
                        }}
                        onError={(e) => console.log(e)}
                      />
                    </div>
                    <button type="button" className="era-cta-line" onClick={() => setPaying(null)}>
                      Voltar
                    </button>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </main>

      <div className="era-meandro" aria-hidden="true" />

      <footer className="era-footer">
        <div className="era-wrap era-footer-grid">
          <div className="era-footer-col">
            <a href="/">Voltar ao site</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MeusIngressos;
